import React, { Fragment } from 'react';
import { observer, inject } from 'mobx-react';

import InputField from '~/common/components/InputField';
import FlexField from '~/common/components/FlexField';
import RadioInputField from '~/common/components/RadioInputField';
import Checkbox from '~/common/components/Checkbox';

import { CC, DD } from '~/common/utils/consts';
import Tracking from '~/common/utils/gaTracking';
import { mapEcommerceItems } from '~/common/utils/trackingHelpers';

// Import images
import cc from '~/common/images/img-icon-credit@2x.png';
import lock from '~/common/images/lock.svg';

// Import Stylesheet
import styles from './payment-info.css';

@inject('formsStore', 'flexFrameStore', 'contentStore', 'checkoutStore')
@observer
export default class PaymentInfo extends React.Component {
    constructor(props) {
        super(props);
        const tracking = new Tracking();
        this.trackCheckoutStep = () => {
            tracking.trackBeginCheckout({
                step: 4,
                ecommerce: mapEcommerceItems({
                    selectedPlan: props.checkoutStore.selectedPlan,
                    billingPeriod: props.formsStore.BillingPeriod,
                    currencyCode: props.contentStore.contentData.CurrencyCode,
                    licenseSelector: props.formsStore.LicenseSelector,
                    selectedAddons: props.checkoutStore.selectedAddons
                })
            });
        };
    }
    componentDidMount() {
        const { flexFrameStore } = this.props;
        flexFrameStore.createFormOnComponentBind();
    }

    componentWillUnmount() {
        const { flexFrameStore } = this.props;
        // tear down the microform
        flexFrameStore.destroyFlexFrame();
    }

    render() {
        const { formsStore } = this.props;

        const {
            ExpirationDate,
            ExpirationDateError,
            ExpirationDateValid,
            PaymentType,
            PayDD,
            PayDDError,
            IBAN,
            IBANError,
            IBANValid,
            BIC,
            BICError,
            BICValid,
            TaxNumber
        } = this.props.formsStore;

        const { labelContent, contentData } = this.props.contentStore;

        const { allowDD } = this.props.checkoutStore;

        const {
            CreditCardFocused,
            CreditCardError,
            CreditCardHasValue,
            CreditCardValid,
            CVVFocused,
            CVVError,
            CVVHasValue,
            CVVValid } = this.props.flexFrameStore;

        if (CreditCardFocused) {
            this.trackCheckoutStep();
        }
        return (
            <div className="payment-info">
                <div className={styles.wrapper}>
                    <div className={styles.heading}>
                        <h2>{labelContent.PaymentDetails}</h2>
                        <span>
                            <img src={lock} /> SECURE
                        </span>
                    </div>
                    <div className={styles.selector}>
                        <RadioInputField>
                            <div className={styles.radio}>
                                <label>
                                    <input
                                        checked={PaymentType === CC}
                                        data-payment='CC' name="paymentType"
                                        onChange={() => formsStore.setFormData('PaymentType', CC)} type="radio"
                                        value={CC}
                                    />
                                    <img
                                        alt="Credit Cards Badge" className={styles['cc-img']}
                                        src={cc} title="ccbadge"
                                    />
                                </label>
                            </div>
                            {allowDD &&
                            <div className={styles.radio}>
                                <label>
                                    <input
                                        checked={PaymentType === DD}
                                        data-payment='DD' name="paymentType"
                                        onChange={() => formsStore.setFormData('PaymentType', DD)} type="radio"
                                        value={DD}
                                    /> {labelContent.DirectDebit}
                                </label>
                            </div>
                            }
                        </RadioInputField>
                    </div>
                    <div className={`${styles['cc-grid']} ${PaymentType === DD ? styles['cc-grid--hidden'] : ''}`}>


                        <FlexField
                            error={CreditCardError}
                            errorCopy={labelContent.ErrorInvalidCreditCardNumber}
                            focused={CreditCardFocused}
                            hasValue={CreditCardHasValue}
                            id="ccnumber"
                            labelCopy={labelContent.CardNumber}
                            valid={CreditCardValid}
                            wrapperClass={styles.cc}
                        />

                        <InputField
                            allowedCharacters="/"
                            defaultValue={ExpirationDate}
                            error={ExpirationDateError}
                            hint="MM/YY"
                            id="exp-date"
                            inputMode="numeric"
                            label={labelContent.ExpirationDate}
                            mask={[/[0-1]/, /\d/, '/', /\d/, /\d/]}
                            name="ExpirationDate"
                            onBlur={() => formsStore.ExpirationDateValidation()}
                            onFocus={() => this.trackCheckoutStep()}
                            pattern="[0-9]*"
                            restrictCharacters="numericOnly"
                            type="tel"
                            valid={ExpirationDateValid}
                            wrapperClass={styles['exp-date']}
                        />

                        <FlexField
                            error={CVVError}
                            errorCopy={'cvv invalid'}
                            focused={CVVFocused}
                            hasValue={CVVHasValue}
                            id="cvv"
                            labelCopy={'Security Code'}
                            valid={CVVValid}
                            wrapperClass={styles.cvv}
                        />
                    </div>
                    <div className={`${styles['dd-grid']} ${PaymentType === CC ? styles['dd-grid--hidden'] : ''}`}>
                        <InputField
                            allowedCharacters=" "
                            defaultValue={IBAN}
                            error={IBANError}
                            id="iban"
                            label={'IBAN'}
                            maxLength={50}
                            name="IBAN"
                            onBlur={() => formsStore.IBANValidation()}
                            onFocus={() => this.trackCheckoutStep()}
                            restrictCharacters="alphaNumericOnly"
                            type="text"
                            valid={IBANValid}
                        />
                        <InputField
                            allowedCharacters=" "
                            defaultValue={BIC}
                            error={BICError}
                            id="bic"
                            label={'BIC'}
                            maxLength={50}
                            name="BIC"
                            onBlur={() => formsStore.BICValidation()}
                            onFocus={() => this.trackCheckoutStep()}
                            restrictCharacters="alphaNumericOnly"
                            type="text"
                            valid={BICValid}
                        />
                    </div>
                    {PaymentType === DD &&
                    <Fragment>
                        <div className={styles.dd}>
                            <Checkbox
                                checked={PayDD}
                                id="dd"
                                name="dd"
                                onClick={() => formsStore.toggleDD()}
                            />
                            <label htmlFor="dd">{labelContent.PayByDirectDebit}</label>
                        </div>
                        {PayDDError &&
                        <p className={styles.error}>{labelContent.ErrorRequiredDirectDebitConfirmation}</p>
                        }
                        <p className={styles['dd-text']}>{labelContent.DirectDebitAgreement}</p>
                    </Fragment>
                    }
                    {contentData.TaxNumberAllowed &&
                    <Fragment>
                        <span className={styles.optional}>{labelContent.Optional}:</span>
                        <InputField
                            allowedCharacters=" "
                            defaultValue={TaxNumber}
                            id="TaxNumber"
                            label={labelContent.VATCode}
                            maxLength={50}
                            name="TaxNumber"
                            onFocus={() => this.trackCheckoutStep()}
                            restrictCharacters="alphaNumericOnly"
                            type="text"
                        />
                    </Fragment>
                    }
                </div>
            </div>
        );
    }
}
